import {useEffect, useMemo} from 'react'
import nunjucks from 'nunjucks'
import {createSelector} from 'reselect'

import {
  setForm,
  updateForm,
  removeForm,
  useSelector,
  onlyIfForm,
  getState,
} from '../../../store.js'
import {
  BarcodeLayouts,
  BarcodeValueOptions,
  BarcodeTextOptions,
  LOCATION_IN_WAREHOUSE_TEMPLATE,
  LOW_STOCK_THRESHOLD_TEMPLATE,
  CUSTOM_TEMPLATE,
  BLANK_TEMPLATE,
} from '../../../common/constants/BarcodeLabel.js'
import {parseNonZeroPositiveNumber} from '../../../common/parseNumbers.js'
import {isPresent} from '../../../common/utils.js'
import ConfirmModal from '../../../common/components/Modal/ConfirmModal.js'
import TextInput from '../../../common/components/TextInput.js'
import NumberInput from '../../../common/components/Form/NumberInput.js'
import ButtonSendToPrinter, {
  sendToPrintNode,
  startSendingToPrintNode,
} from '../../../common/components/Button/ButtonSendToPrinter.js'
import Zelect from '../../../common/components/Zelect.js'
import {warehouseOptionsSelector} from '../../../data/warehouses.js'
import {barcodePrinterIDSelector} from '../../../data/printerSettings.js'
import {currencySymbolSelector} from '../../../data/company.js'
import {productsSelector} from '../../../data/products.js'
import {formsSelector} from '../../../redux/selectors/ui/forms.js'
import {
  LAYOUT_BARCODE,
  layoutsByTypeSortedByName,
} from '../../../data/layout.js'
import Checkbox from '../../../common/components/Checkbox.js'
import {saveUserSettings, userSettingSelector} from '../../../data/me.js'
import {Count, Plural, PluralBlock} from '../../../common/components/Plural.js'
import {abodeRenderPOST} from '../../../common/abode.js'
import {openFileInTab} from '../../../common/openInTab.js'
import {openSyncLoadingTab} from '../../AbodeForm/abodeFormFunctions.js'

const templateEnv = new nunjucks.Environment([], {autoescape: false})

export const MODAL_FORM = 'barcode_label_modal'

export function barcodeLabelModalSettingsSelector(state) {
  return userSettingSelector(state, {
    setting: MODAL_FORM,
    defaultValue: {
      layout: BarcodeLayouts[0].value,
      valueType: BarcodeValueOptions[0].value,
      showValue: true,
      customValue: '',
      line1Type: BarcodeTextOptions[0].value,
      customLine1: '',
      line2Type: BarcodeTextOptions[0].value,
      customLine2: '',
      layoutID: null,
      warehouseID: null,
      copyCount: '1',
    },
  })
}

export function showBarcodeLabelModal(skus, counts) {
  setForm(MODAL_FORM, {
    skus,
    counts,
    ...barcodeLabelModalSettingsSelector(getState()),
  })
}

export function updateModalForm(...args) {
  updateForm(MODAL_FORM, ...args)
}

function saveBarcodeLabelModalSettings() {
  const form = modalFormSelector(getState())

  if (!form) {
    return
  }

  const {
    layout,
    valueType,
    showValue,
    customValue,
    line1Type,
    customLine1,
    line2Type,
    customLine2,
    layoutID,
    warehouseID,
    copyCount,
  } = form

  saveUserSettings({
    [MODAL_FORM]: {
      layout,
      valueType,
      showValue,
      customValue,
      line1Type,
      customLine1,
      line2Type,
      customLine2,
      layoutID,
      warehouseID,
      copyCount,
    },
  })
}

export function closeModal(outcome) {
  if (outcome) {
    saveBarcodeLabelModalSettings()
  }

  removeForm(MODAL_FORM)
}

export function modalFormSelector(state) {
  return formsSelector(state)[MODAL_FORM]
}

export const productParamsSelector = createSelector(
  modalFormSelector,
  productsSelector,
  currencySymbolSelector,
  ({skus, warehouseID}, products, currencySymbol) =>
    skus.map((sku) => {
      const product = products[sku]

      return {
        sku: product?.sku || '',
        name: product?.name || '',
        upc: product?.upc || '',
        asin: product?.asin || '',
        cost: product?.cost || 0,
        price: product?.price || 0,
        currency_symbol: currencySymbol,
        ...(() => {
          if (!warehouseID) {
            return {}
          }

          const warehouse = product?.warehouses?.find(
            ({id}) => id === warehouseID,
          )

          return {
            location_in_warehouse: warehouse?.location_in_warehouse || '',
            low_stock_threshold: warehouse?.low_stock_threshold || '',
          }
        })(),
      }
    }),
)

export const valuesSelector = createSelector(
  modalFormSelector,
  productParamsSelector,
  ({valueType, customValue}, products) => {
    try {
      const template = nunjucks.compile(
        valueType === CUSTOM_TEMPLATE ? customValue : valueType,
        templateEnv,
      )

      return products.map((product) => template.render(product))
    } catch (err) {
      return products.map(() => err.message)
    }
  },
)

export const lines1Selector = createSelector(
  modalFormSelector,
  productParamsSelector,
  ({line1Type, customLine1}, products) => {
    try {
      const template = nunjucks.compile(
        line1Type === CUSTOM_TEMPLATE ? customLine1 : line1Type,
        templateEnv,
      )

      return products.map((product) => template.render(product))
    } catch (err) {
      return products.map(() => err.message)
    }
  },
)

export const lines2Selector = createSelector(
  modalFormSelector,
  productParamsSelector,
  ({line2Type, customLine2}, products) => {
    try {
      const template = nunjucks.compile(
        line2Type === CUSTOM_TEMPLATE ? customLine2 : line2Type,
        templateEnv,
      )

      return products.map((product) => template.render(product))
    } catch (err) {
      return products.map(() => err.message)
    }
  },
)

export const barcodeLabelParamsSelector = createSelector(
  modalFormSelector,
  valuesSelector,
  lines1Selector,
  lines2Selector,
  (
    {layout, showValue, copyCount, layoutID, counts},
    values,
    lines1,
    lines2,
  ) => {
    if (values.length === 0) {
      return null
    }

    const params = {
      render: [
        'Barcode',
        {
          layout,
          copyCount: parseNonZeroPositiveNumber(copyCount) || 1,
          showValue,
          values: values.map((value, index) => [
            value,
            lines1[index],
            lines2[index],
            ...(counts ? [counts[index]] : []),
          ]),
        },
      ],
    }

    if (layoutID) {
      params.layout_id = layoutID
    }

    return params
  },
)

async function renderBarcodeLabels(tab) {
  const params = barcodeLabelParamsSelector(getState())

  const file = await abodeRenderPOST(params)

  if (tab) {
    openFileInTab(file, tab)

    closeModal(true)
  }

  return file
}

function openBarcodeLabelsInTab() {
  const tab = openSyncLoadingTab()

  if (!tab) {
    return
  }

  renderBarcodeLabels(tab)
}

async function sendBarcodeLabelsToPrintNode({
  canPrint,
  printButtonFormKey,
  printerID,
  documentCount,
}) {
  if (!canPrint) {
    return
  }

  startSendingToPrintNode(printButtonFormKey)

  const file = await renderBarcodeLabels()

  await sendToPrintNode({
    formKey: printButtonFormKey,
    pdfFile: file,
    pdfName: file.name,
    printerID,
    documentCount,
  })

  closeModal(true)
}

export const barcodeLabelModalErrorsSelector = createSelector(
  modalFormSelector,
  valuesSelector,
  ({layout, showValue, line1Type, line2Type}, values) => {
    const errors = {}

    if (values.filter((value) => !isPresent(value)).length !== 0) {
      errors.valueType = 'Not all SKUs have values for that given type'
    }

    if (
      layout === BarcodeLayouts[0].value &&
      [
        showValue,
        line1Type !== BLANK_TEMPLATE,
        line2Type !== BLANK_TEMPLATE,
      ].filter((v) => v).length > 2
    ) {
      errors.line2Type = `The ${BarcodeLayouts[0].display} size can not fit 3 lines of text. Uncheck “Display Barcode Value” or set Additional Line 2 to “None.”`
      errors.preventSave = true
    }

    return errors
  },
)

export const needsWarehouseSelector = createSelector(
  modalFormSelector,
  ({valueType, line1Type, line2Type}) =>
    !![valueType, line1Type, line2Type].find((type) =>
      [
        LOCATION_IN_WAREHOUSE_TEMPLATE,
        LOW_STOCK_THRESHOLD_TEMPLATE,
        CUSTOM_TEMPLATE,
      ].includes(type),
    ),
)

function updateLayoutID(layoutID) {
  updateModalForm({layoutID: Number(layoutID) || null})
}

function BarcodeLabelModal({form}) {
  const errors = useSelector(barcodeLabelModalErrorsSelector)
  const warehouseOptions = useSelector(warehouseOptionsSelector)
  const values = useSelector(valuesSelector)
  const lines1 = useSelector(lines1Selector)
  const lines2 = useSelector(lines2Selector)
  const needsWarehouse = useSelector(needsWarehouseSelector)
  const barcodePrinterID = useSelector(barcodePrinterIDSelector)
  const barcodeLayouts =
    useSelector(layoutsByTypeSortedByName)[LAYOUT_BARCODE] || []
  const barcodeLayoutOptions = useMemo(
    () => [
      {value: '', display: 'Standard'},
      ...barcodeLayouts.map((layout) => ({
        value: layout.id,
        display: layout.name,
      })),
    ],
    [barcodeLayouts],
  )

  useEffect(() => {
    if (
      !form.warehouseID ||
      !warehouseOptions.find(({value}) => value === form.warehouseID)
    ) {
      updateModalForm({
        warehouseID: warehouseOptions?.[0]?.value || '',
      })
    }
  }, [form.warehouseID])

  useEffect(() => {
    if (form.layoutID && !barcodeLayouts.find(({id}) => id === form.layoutID)) {
      updateLayoutID(null)
    }
  }, [form.layoutID])

  const totalCount = useMemo(() => {
    const {skus, counts} = form

    const countFromSKUs = counts
      ? counts.reduce((prev, count) => prev + count, 0)
      : skus.length

    return countFromSKUs * (Number(form.copyCount) || 0)
  }, [form.copyCount])

  return (
    <ConfirmModal
      title="Print Barcode Label"
      modalSize="sm"
      onConfirm={openBarcodeLabelsInTab}
      onCancel={() => closeModal(false)}
      confirmText="View/Print"
      cancelText="Cancel"
      MiddleButtons={() => (
        <div className="margin-right-10">
          <ButtonSendToPrinter
            className="margin-bottom-5 meta-barcodelabelmodal-button-instaprint"
            title="Barcode Label"
            printerID={barcodePrinterID}
            documentCount={totalCount}
            isDisabled={errors.preventSave}
            onClick={({canPrint, printButtonFormKey}) =>
              sendBarcodeLabelsToPrintNode({
                canPrint,
                printButtonFormKey,
                printerID: barcodePrinterID,
                documentCount: totalCount,
              })
            }
          />
        </div>
      )}
      isDisabled={errors.preventSave}
    >
      <dl className="list">
        {form.skus.length == 1 && (
          <dt className="list__title--label-success lh-md margin-top-0 margin-bottom-15">
            <strong>SKU:</strong> <span className="unbold">{form.skus[0]}</span>
          </dt>
        )}
        <dd className="list__item margin-bottom-15">
          <PluralBlock count={totalCount}>
            <Count /> total barcode <Plural word="label" /> will be generated
            for{' '}
          </PluralBlock>
          <PluralBlock array={form.skus}>
            <Count /> product <Plural word="SKU" />.
          </PluralBlock>
        </dd>
        <dd className="list__item margin-bottom-15">
          <Zelect
            label="Size"
            id="layout"
            value={form.layout}
            onChange={({value}) => updateModalForm({layout: value})}
            options={BarcodeLayouts}
          />
        </dd>
        {barcodeLayouts.length > 0 && (
          <dd className="list__item margin-bottom-15">
            <Zelect
              label="Layout"
              id="layout_id"
              value={form.layoutID || ''}
              onChange={({value}) => updateLayoutID(value)}
              options={barcodeLayoutOptions}
            />
          </dd>
        )}
        <dd className="list__item">
          <Zelect
            label="Barcode Value"
            id="valueType"
            value={form.valueType}
            onChange={({value}) => updateModalForm({valueType: value})}
            errorMessage={errors.valueType}
            options={BarcodeTextOptions}
          />
          {values.length && !errors.valueType && (
            <div className="fs-n1 text--md-grey">Example: {values[0]}</div>
          )}
        </dd>
        {form.valueType === CUSTOM_TEMPLATE && (
          <dd className="list__item">
            <TextInput
              label="Custom Barcode"
              id="customValue"
              value={form.customValue}
              onChange={(value) => updateModalForm({customValue: value})}
            />
          </dd>
        )}
        <dd className="list__item">
          <Checkbox
            label="Display Barcode Value"
            id="showValue"
            labelClassName="margin-bottom-15"
            checked={form.showValue}
            onChange={(showValue) => updateModalForm({showValue})}
          />
        </dd>
        <dd className="list__item">
          <Zelect
            label="Additional Line 1"
            id="line1Type"
            value={form.line1Type}
            onChange={({value}) => {
              const updates = {line1Type: value}

              if (value === BLANK_TEMPLATE) {
                updates.line2Type = BLANK_TEMPLATE
              }

              updateModalForm(updates)
            }}
            options={BarcodeTextOptions}
          />
          {lines1.length && (
            <div className="fs-n1 text--md-grey">
              Example: {lines1[0] || 'n/a'}
            </div>
          )}
        </dd>
        {form.line1Type === CUSTOM_TEMPLATE && (
          <dd className="list__item">
            <TextInput
              label="Custom Line 1"
              id="customLine1"
              className="w-50 input--med-h margin-bottom-15"
              value={form.customLine1}
              onChange={(value) => updateModalForm({customLine1: value})}
            />
          </dd>
        )}
        {form.line1Type !== BLANK_TEMPLATE && (
          <dd className="list__item w-75">
            <Zelect
              label="Additional Line 2"
              id="line2Type"
              value={form.line2Type}
              onChange={({value}) => updateModalForm({line2Type: value})}
              errorMessage={errors.line2Type}
              options={BarcodeTextOptions}
            />
            {lines2.length && !errors.line2Type && (
              <div className="fs-n1 text--md-grey">
                Example: {lines2[0] || 'n/a'}
              </div>
            )}
          </dd>
        )}
        {form.line2Type === CUSTOM_TEMPLATE && (
          <dd className="list__item">
            <TextInput
              label="Custom Line 2"
              id="customLine2"
              className="w-50 input--med-h margin-bottom-15"
              value={form.customLine2}
              onChange={(value) => updateModalForm({customLine2: value})}
            />
          </dd>
        )}
        {needsWarehouse && (
          <dd className="list__item">
            <Zelect
              label="Warehouse"
              id="warehouseID"
              dropdownClassName="margin-bottom-15"
              value={form.warehouseID || ''}
              onChange={({value}) => updateModalForm({warehouseID: value})}
              options={warehouseOptions}
            />
          </dd>
        )}
        <dd className="list__item">
          <label htmlFor="copy_count">No. of Copies</label>
          <NumberInput
            id="copyCount"
            value={form.copyCount}
            onChange={(value) =>
              updateModalForm({
                copyCount: `${value}`,
              })
            }
            min={1}
          />
        </dd>
      </dl>
    </ConfirmModal>
  )
}

export default onlyIfForm(BarcodeLabelModal, modalFormSelector)
